import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { 
  MessageCircle, 
  Send, 
  ArrowLeft, 
  Mail, 
  Clock, 
  CheckCircle2,
  Loader2,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function UserMessages() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newContent, setNewContent] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/messages/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
      }
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/messages/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  };

  const fetchMessages = async (conversationId) => {
    setLoadingMessages(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/messages/conversation/${conversationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
        fetchUnreadCount(); // Refresh unread count
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const sendNewMessage = async () => {
    if (!newSubject.trim() || !newContent.trim()) {
      toast.error('Please enter subject and message');
      return;
    }
    
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/messages/send`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          subject: newSubject,
          content: newContent
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Message sent! We\'ll respond as soon as possible.');
        setShowNewMessage(false);
        setNewSubject('');
        setNewContent('');
        fetchConversations();
        setSelectedConversation(data.conversation_id);
        fetchMessages(data.conversation_id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send message');
      }
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const sendReply = async () => {
    if (!replyContent.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/messages/conversation/${selectedConversation}/reply`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ content: replyContent })
      });
      
      if (response.ok) {
        toast.success('Reply sent!');
        setReplyContent('');
        fetchMessages(selectedConversation);
        fetchConversations();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send reply');
      }
    } catch (err) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    fetchConversations();
    fetchUnreadCount();
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation);
    }
  }, [selectedConversation]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {selectedConversation && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setSelectedConversation(null)}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
          )}
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-primary" />
              Messages
              {unreadCount > 0 && (
                <Badge variant="destructive" className="ml-2">{unreadCount} new</Badge>
              )}
            </h2>
            <p className="text-sm text-muted-foreground">
              {selectedConversation ? 'Conversation' : 'Contact support'}
            </p>
          </div>
        </div>
        
        {!selectedConversation && (
          <Button onClick={() => setShowNewMessage(true)} data-testid="new-message-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Message
          </Button>
        )}
      </div>

      {/* Conversations List */}
      {!selectedConversation && (
        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : conversations.length > 0 ? (
            conversations.map((conv) => (
              <Card 
                key={conv.conversation_id}
                className={`cursor-pointer hover:border-primary/50 transition-colors ${conv.unread_count > 0 ? 'border-primary/30 bg-primary/5' : ''}`}
                onClick={() => setSelectedConversation(conv.conversation_id)}
                data-testid={`conversation-${conv.conversation_id}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium truncate">{conv.subject}</h3>
                        {conv.unread_count > 0 && (
                          <Badge variant="destructive" className="text-xs">{conv.unread_count}</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground truncate mt-1">{conv.last_message}</p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {new Date(conv.updated_at).toLocaleString()}
                        <span className="mx-1">•</span>
                        {conv.last_sender_type === 'admin' ? (
                          <Badge variant="outline" className="text-xs">Support replied</Badge>
                        ) : (
                          <span>You</span>
                        )}
                      </div>
                    </div>
                    <Mail className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="bg-card/50">
              <CardContent className="py-12 text-center">
                <MessageCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">No messages yet</p>
                <Button onClick={() => setShowNewMessage(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Start a Conversation
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Conversation View */}
      {selectedConversation && (
        <Card className="bg-card/50">
          <CardContent className="py-4">
            {loadingMessages ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                <div className="space-y-4 max-h-96 overflow-y-auto mb-4 p-2">
                  {messages.map((msg) => (
                    <div 
                      key={msg.message_id}
                      className={`p-4 rounded-lg ${
                        msg.sender_type === 'admin' 
                          ? 'bg-primary/10 border-l-4 border-primary ml-4' 
                          : 'bg-slate-800/50 mr-4'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`font-medium ${msg.sender_type === 'admin' ? 'text-primary' : ''}`}>
                          {msg.sender_name}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(msg.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  ))}
                </div>
                
                {/* Reply Box */}
                <div className="border-t pt-4">
                  <Textarea
                    placeholder="Type your reply..."
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    rows={3}
                    className="mb-3"
                    data-testid="reply-textarea"
                  />
                  <Button 
                    onClick={sendReply} 
                    disabled={sending || !replyContent.trim()}
                    data-testid="send-reply-btn"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send Reply
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* New Message Dialog */}
      <Dialog open={showNewMessage} onOpenChange={setShowNewMessage}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Message to Support</DialogTitle>
            <DialogDescription>
              Send us a message and we'll respond as soon as possible.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Subject</Label>
              <Input 
                placeholder="What's this about?"
                value={newSubject}
                onChange={(e) => setNewSubject(e.target.value)}
                data-testid="new-message-subject"
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                placeholder="Type your message..."
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                rows={5}
                data-testid="new-message-content"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowNewMessage(false)}>
                Cancel
              </Button>
              <Button 
                onClick={sendNewMessage} 
                disabled={sending}
                data-testid="send-new-message-btn"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Message
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default UserMessages;
